<template>
  <button v-if="showDrivePicker"
          type="button"
          aria-label="Upload file from Google Drive"
          :disabled="!pickerInitialized || !gsiInitialized || loading"
          @click="handleAuth">
    <slot/>
  </button>
</template>

<script setup>
/* eslint-disable */
import {onBeforeUnmount, onMounted, ref} from "vue";
import {loadScript} from "vue-plugin-load-script";

const emits = defineEmits(["update:file", "update:loading"]);
const props = defineProps({
  mimes: {
    type: String,
    default: "image/png,image/jpeg,image/jpg",
  },
  filename: {
    type: String,
    default: "google-drive-upload",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const showDrivePicker = ref(true);
const tokenClient = ref(null);
const accessToken = ref(null);
const pickerInitialized = ref(false);
const gsiInitialized = ref(false);
const picker = ref(null);

const APP_ID = import.meta.env.VITE_GOOGLE_PROJECT_NUMBER;
const OAUTH_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
const API_KEY = import.meta.env.VITE_GOOGLE_PICKER_API_KEY;
const SCOPE = "https://www.googleapis.com/auth/drive.file";
const DRIVE_DISCOVERY_DOCS = "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest";
const GSI_SCRIPT = "https://accounts.google.com/gsi/client";
const GAPI_SCRIPT = "https://apis.google.com/js/api.js";

/**
 * Load Google Picker API
 *
 * @returns {Promise<void>}
 */
const loadPickerApi = async () => {
  await loadScript(GAPI_SCRIPT);
  gapi.load("picker", () => pickerInitialized.value = true);

  gapi.load("client", () => {
    gapi.client.init({
      apiKey: API_KEY,
      discoveryDocs: [DRIVE_DISCOVERY_DOCS],
    });
  });
};

/**
 * Initialize Google Identity Services
 *
 * @returns {Promise<void>}
 */
const initGsi = async () => {
  await loadScript(GSI_SCRIPT);
  tokenClient.value = google.accounts.oauth2.initTokenClient({
    client_id: OAUTH_CLIENT_ID,
    scope: SCOPE,
    callback: "", // Set later, per Google's documentation
    errorCallback: (error) => {
      console.error("Error initializing Google Identity Services:", error);
    },
  });
  gsiInitialized.value = true;
};

/**
 *  Sign in the user upon button click.
 */
const handleAuth = () => {
  if (!pickerInitialized.value || !gsiInitialized.value) return;

  if (accessToken.value) {
    createPicker();
    return;
  }

  tokenClient.value.callback = async (response) => {
    if (response.error !== undefined) {
      throw (response);
    }

    accessToken.value = response.access_token;
    await createPicker();
  };

  if (!accessToken.value) {
    // Prompt the user to select a Google Account and ask for consent to share their data
    // when establishing a new session.
    tokenClient.value.requestAccessToken({prompt: "consent"});
  } else {
    // Skip display of account chooser and consent dialog for an existing session.
    tokenClient.value.requestAccessToken({prompt: ""});
  }
};

/**
 *  Sign out the user upon button click.
 */
const handleSignOut = () => {
  if (accessToken.value) {
    google.accounts.oauth2.revoke(accessToken.value);
    accessToken.value = null;
  }
};

/**
 *  Create and render a Picker object for searching images.
 */
const createPicker = () => {
  const view = new google.picker.View(google.picker.ViewId.DOCS);
  view.setMimeTypes(props.mimes);

  picker.value = new google.picker.PickerBuilder()
    .enableFeature(google.picker.Feature.NAV_HIDDEN)
    .setDeveloperKey(API_KEY)
    .setAppId(APP_ID)
    .setOAuthToken(accessToken.value)
    .setOrigin(window.location.protocol + "//" + window.location.host)
    .addView(view)
    .setCallback(pickerCallback)
    .build();

  picker.value.setVisible(true);
};

/**
 * Displays the file details of the user's selection.
 * @param {object} data - Containers the user selection from the picker
 */
const pickerCallback = async (data) => {
  if (data.action !== google.picker.Action.PICKED) return;

  emits("update:loading", true);

  const fileId = data[google.picker.Response.DOCUMENTS][0][google.picker.Document.ID];
  let mimeType = data[google.picker.Response.DOCUMENTS][0][google.picker.Document.MIME_TYPE];

  let response;

  try {
    if (mimeType === "application/vnd.google-apps.document") {
      response = await gapi.client.drive.files.export({
        fileId: fileId,
        mimeType: "application/pdf"
      });

      mimeType = "application/pdf";
    } else {
      response = await gapi.client.drive.files.get({
        fileId: fileId,
        alt: "media"
      });
    }

    const body = response.body;
    const typedarray = new Uint8Array(body.length).map((_, i) => body.charCodeAt(i));

    const extensions = {
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
      "application/msword": "doc",
      "text/rtf": "rtf",
      "text/plain": "txt",
      "application/pdf": "pdf",
    };

    const filenameAndExtension = props.filename + "." + (extensions[mimeType] || "");

    const file = new File([typedarray], filenameAndExtension, {type: mimeType});

    emits("update:file", file);
    emits("update:loading", false);
  } catch (error) {
    toast.error("Error retrieving file from Google Drive");
    console.error("Error retrieving file from Google Drive:", error);
    emits("update:loading", false);
  }
};

onMounted(async () => {
  try {
    await loadPickerApi();
    await initGsi();
  } catch (error) {
    showDrivePicker.value = false;
    console.error("Error initializing Google Picker:", error);
  }

  window.addEventListener("beforeunload", handleSignOut);
});

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", handleSignOut);
  handleSignOut();
});
</script>

<style>
.picker-dialog-bg {
  z-index: 2147483646 !important;
  background-color: rgba(0, 0, 0, 0.8) !important;
}

.picker-dialog {
  z-index: 2147483647 !important;
  border-radius: 0.5rem !important;
}
</style>
